// Reusable Schema.org JSON-LD fragments for RealtyIQ.io.
//
// These are imported by individual public pages so each route can declare the
// structured data Google needs to render rich results — without duplicating the
// canonical Organization / SoftwareApplication payload across every page.
//
// Anything that is per-page (FAQs, ContactPoints, breadcrumb position) lives
// next to the page; anything that is product-level (brand name, logo, social
// profiles, support email) lives here so there's one place to update it.

export const SITE_URL = 'https://realtyiq.io';

export const SITE_NAME = 'RealtyIQ.io';

// One-liner description used for og:description / meta description fallbacks.
// Keep under 160 chars — Google truncates beyond that.
export const SITE_DESCRIPTION =
  'AI platform for real estate agents — photo staging, listing descriptions, single-property websites, and CRM in one place. Replaces 3 tools, from $59/mo.';

// Default OG image (1200x630). Generated 2026-05-12, lives in /public so the URL
// stays stable across deploys.
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

// Organization schema — used on every public page so Google understands who
// publishes the site and links sitelinks back to the canonical entity.
//
// `@id` is anchored to ${SITE_URL}/#organization so that the WebSite schema
// below (and any future BreadcrumbList / Product schemas that need a
// `publisher`) can reference it by URI without duplicating the payload.
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.svg`,
  description: SITE_DESCRIPTION,
  sameAs: [
    'https://twitter.com/realtyiq',
    'https://www.linkedin.com/company/realtyiq',
  ],
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@realtyiq.io',
      availableLanguage: ['English'],
      areaServed: 'US',
    },
    {
      '@type': 'ContactPoint',
      contactType: 'sales',
      email: 'sales@realtyiq.io',
      availableLanguage: ['English'],
      areaServed: 'US',
    },
  ],
};

// WebSite schema — gives Google the canonical site name and enables the
// optional sitelinks search box. References the Organization schema above by
// `@id`; both must be emitted on the same page (Landing) for the reference
// to resolve. If you emit websiteSchema without organizationSchema, Google
// silently drops the publisher.
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { '@id': `${SITE_URL}/#organization` },
  inLanguage: 'en-US',
};

// SoftwareApplication schema — top-level product description, no per-plan
// offers (those live on Landing where pricing is rendered). Pages that just
// need to declare "this site is software" can reuse this fragment.
export const softwareApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: SITE_NAME,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: SITE_URL,
  description: SITE_DESCRIPTION,
};

// Editorial author attribution for AI-assisted content.
//
// Every post produced by the SEO content-automation suite (auto-blog generator,
// IQ Score educational pages, state compliance hubs) is drafted by AI and then
// reviewed + published by a human before it goes live. Both Google's helpful-
// content guidance and the FTC's AI-disclosure stance want that authorship made
// explicit, so we attribute these posts to a named editorial entity rather than
// leaving `author` blank. `buildBlogPostingSchema` defaults to this name.
export const EDITORIAL_AUTHOR_NAME = 'RealtyIQ Editorial';

// schema.org Person/Organization fragment for the byline. Modelled as an
// Organization (it's a team byline, not a single person) and linked back to the
// publisher Organization so Google resolves the relationship.
export const editorialAuthorSchema = {
  '@type': 'Organization',
  name: EDITORIAL_AUTHOR_NAME,
  url: `${SITE_URL}/blog`,
};

export interface BlogPostingSchemaInput {
  /** Post title → schema.org `headline`. */
  title: string;
  /** Meta description → schema.org `description`. */
  description: string;
  /** Public slug; the canonical `/blog/:slug` URL is derived from it. */
  slug: string;
  /** Publish time (epoch ms) → ISO `datePublished`. */
  datePublished: number;
  /** Last-modified time (epoch ms) → ISO `dateModified`. Defaults to datePublished. */
  dateModified?: number;
  /** Absolute URL to the hero/cover image, when present. */
  image?: string | null;
  /** Byline. Defaults to {@link EDITORIAL_AUTHOR_NAME}. */
  authorName?: string;
}

// Build a BlogPosting JSON-LD object for a single blog post. Unlike the inline
// schema that used to live in the page component, this always emits an `author`
// (attribution is mandatory for our AI-assisted content) and a `publisher`
// linked to the canonical Organization `@id`, which is what enables the
// article-with-byline rich result in Google.
export function buildBlogPostingSchema(input: BlogPostingSchemaInput) {
  const url = `${SITE_URL}/blog/${input.slug}`;
  const published = new Date(input.datePublished).toISOString();
  const modified = new Date(input.dateModified ?? input.datePublished).toISOString();
  const authorName = input.authorName?.trim() || EDITORIAL_AUTHOR_NAME;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: input.title,
    description: input.description,
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    datePublished: published,
    dateModified: modified,
    author: { '@type': 'Organization', name: authorName, url: `${SITE_URL}/blog` },
    publisher: { '@id': `${SITE_URL}/#organization` },
    ...(input.image ? { image: input.image } : {}),
  };
}

// Helper to build a BreadcrumbList for any page. Pass an ordered list of
// {name, path} entries; the last item is the current page. The schema places
// each item at its 1-indexed position.
export function buildBreadcrumbSchema(
  items: Array<{ name: string; path: string }>,
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: item.path.startsWith('http') ? item.path : `${SITE_URL}${item.path}`,
    })),
  };
}
